import { GoogleLogin } from '@react-oauth/google';
import React, { ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import * as routes from '../utils/routes';
import { RegisterFields } from '../types';
import { CurrencyCode } from '../types/currencies';
import ReactGA from 'react-ga4';

const initialState: RegisterFields = {
	firstname: '',
	surname: '',
	email: '',
	password: '',
	shop_name: '',
	currency: CurrencyCode.USD,
};

const Register = () => {
	const [registerFields, setRegisterFields] = React.useState<RegisterFields>(initialState);
	const [isPosting, setIsPosting] = React.useState(false);
	const [showPassword, setShowPassword] = React.useState(false);

	const navigate = useNavigate();

	React.useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/register',
			title: 'Register',
		});
	}, []);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsPosting(true);

		apiClient.auth
			.register(registerFields)
			.then(() => {
				setIsPosting(false);
				navigate(routes.DASHBOARD);
			})
			.catch(() => {
				setIsPosting(false);
			});
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		setRegisterFields({ ...registerFields, [e.target.name]: e.target.value });
	};

	const handleGoogleSignUp = (response: any) => {
		apiClient.auth
			.loginViaGoogle(response)
			.then(() => {
				navigate(routes.DASHBOARD);
			})
			.catch((error: any) => {
				console.error('Google signup error:', error);
				// Error notification is already handled by the API client interceptor
			});
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<div className="min-h-screen bg-white sm:bg-[#f9fafb] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="absolute top-8 left-1/2 transform -translate-x-1/2 md:fixed md:left-8 md:transform-none">
				<Link to="/" className="flex items-center gap-2 justify-center">
					<span className="text-xl md:text-2xl font-bold text-gray-900">
						Vend<span style={{ color: '#3a5bd9' }}>Ready</span>
					</span>
				</Link>
			</div>

			<div
				className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 md:mt-20 md:mt-0"
				style={{ width: '100%' }}
			>
				<div className="w-full">
					<div className="md:bg-white md:shadow-md shadow-none rounded-md p-6 sm:p-8 md:p-12 w-full max-w-md mx-auto">
						<h2 className="text-center text-2xl md:text-3xl font-bold text-gray-900 mb-2">
							Create Your Account
						</h2>
						<p className="text-center text-gray-600 mb-8">Get started with your free trial today</p>

						<div className="space-y-6">
							{/* Google Login */}
							<div className="space-y-4" style={{ width: '350px', margin: '0px auto' }}>
								<GoogleLogin
									width={350}
									onSuccess={handleGoogleSignUp}
									onError={() => {
										console.log('login failed');
									}}
									text="signup_with"
								/>
							</div>

							<div className="relative flex items-center justify-center my-6">
								<div className="border-t border-gray-300 w-full absolute"></div>
								<span className="bg-white px-4 text-sm text-gray-500 relative">
									or sign up with email
								</span>
							</div>

							{/* Email Registration Form */}
							<form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
								<div>
									<label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
										First Name
									</label>
									<input
										id="firstname"
										type="text"
										name="firstname"
										placeholder="Enter your first name"
										value={registerFields.firstname}
										onChange={handleChange}
										className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors duration-200"
										required
										autoComplete="given-name"
									/>
								</div>
								<div>
									<label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-1">
										Last Name
									</label>
									<input
										id="surname"
										type="text"
										name="surname"
										placeholder="Enter your last name"
										value={registerFields.surname}
										onChange={handleChange}
										className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors duration-200"
										required
										autoComplete="family-name"
									/>
								</div>
								<div>
									<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
										Email Address
									</label>
									<input
										id="email"
										type="email"
										name="email"
										placeholder="Enter your email"
										value={registerFields.email}
										onChange={handleChange}
										className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors duration-200"
										required
										autoComplete="email"
									/>
								</div>
								<div className="relative">
									<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
										Password
									</label>
									<div className="relative">
										<input
											id="password"
											type={showPassword ? 'text' : 'password'}
											name="password"
											placeholder="Create a password"
											value={registerFields.password}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors duration-200"
											required
											autoComplete="new-password"
										/>
										<button
											type="button"
											onClick={togglePasswordVisibility}
											className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
										>
											{showPassword ? (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
													/>
												</svg>
											) : (
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
													/>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
													/>
												</svg>
											)}
										</button>
									</div>
								</div>

								<button
									type="submit"
									disabled={isPosting}
									className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									{isPosting ? 'Creating Account...' : 'Create Account'}
								</button>
							</form>

							<p className="mt-6 text-center text-gray-600">
								Already have an account?{' '}
								<Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
									Sign in
								</Link>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Register;
